// src/app/store/themeStore.ts
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { ThemeConfig } from 'antd';
import {
  type ThemeMode,
  type ThemeSchemeKey,
  normalizeThemeScheme,
  buildSchemeThemeConfig,
} from '../theme/themeScheme';

// --- DEPLOYMENT VERSION ---
// Increment this version number on each UI design deployment
// This will automatically clear old theme cache from localStorage
const THEME_STORAGE_VERSION = '1.0.1';

const userTypeToScheme: Record<UserType, ThemeSchemeKey> = {
  system: 'ocean',
  customer: 'ocean',
  company: 'emerald',
  medical: 'sunset',
  user: 'violet',
};

// New: explicit user types and list
export type UserType = 'system' | 'customer' | 'company' | 'medical' | 'user';
export const USER_TYPES = ['system', 'customer', 'company', 'medical', 'user'] as const;

interface ThemeState {
  activeScheme: ThemeSchemeKey;
  themeConfig: ThemeConfig;
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  setActiveScheme: (scheme: ThemeSchemeKey) => void;
  setThemeConfig: (config: ThemeConfig) => void;
  resetTheme: () => void;
  setUserTypeTheme: (userType: UserType) => void;
}

// --- CUSTOM STORAGE WITH VERSION CHECK ---
// Automatically clears old theme cache when THEME_STORAGE_VERSION changes
const createVersionedStorage = () => {
  const versionKey = 'insurance-theme-storage-version';
  const themeKey = 'insurance-theme-storage';

  return {
    getItem: (name: string) => {
      if (name === themeKey) {
        const storedVersion = localStorage.getItem(versionKey);
        if (storedVersion !== THEME_STORAGE_VERSION) {
          // Version mismatch: clear old theme storage on deployment
          console.log(`Theme cache cleared: version updated from ${storedVersion} to ${THEME_STORAGE_VERSION}`);
          localStorage.removeItem(themeKey);
          localStorage.setItem(versionKey, THEME_STORAGE_VERSION);
          return null;
        }
      }
      return localStorage.getItem(name);
    },
    setItem: (name: string, value: string) => {
      if (name === themeKey) {
        localStorage.setItem(versionKey, THEME_STORAGE_VERSION);
      }
      localStorage.setItem(name, value);
    },
    removeItem: (name: string) => {
      if (name === themeKey) {
        localStorage.removeItem(versionKey);
      }
      localStorage.removeItem(name);
    },
  };
};

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      activeScheme: 'ocean',
      themeConfig: buildSchemeThemeConfig('ocean', 'light'),
      mode: 'light',
      setMode: (mode) => {
        const { activeScheme, themeConfig } = get();
        set({
          mode,
          themeConfig: buildSchemeThemeConfig(activeScheme, mode, themeConfig),
        });
      },
      setActiveScheme: (scheme) => {
        const normalized = normalizeThemeScheme(scheme);
        const { mode } = get();
        set({
          activeScheme: normalized,
          themeConfig: buildSchemeThemeConfig(normalized, mode),
        });
      },
      setThemeConfig: (config) => {
        const { mode, activeScheme } = get();
        set({
          themeConfig: buildSchemeThemeConfig(activeScheme, mode, config),
        });
      },
      resetTheme: () =>
        set((state) => ({
          themeConfig: buildSchemeThemeConfig(state.activeScheme, state.mode),
        })),
      setUserTypeTheme: (userType: UserType) => {
        const { mode } = get();
        const scheme = userTypeToScheme[userType] ?? 'ocean';
        set({
          activeScheme: scheme,
          themeConfig: buildSchemeThemeConfig(scheme, mode),
        });
      },
    }),
    {
      name: 'insurance-theme-storage',
      storage: createJSONStorage(() => createVersionedStorage()),
      partialize: (state) => ({
        mode: state.mode,
        activeScheme: state.activeScheme,
        themeConfig: state.themeConfig,
      }),
      migrate: (persistedState) => {
        if (!persistedState || typeof persistedState !== 'object') {
          return {
            activeScheme: 'ocean',
            mode: 'light',
            themeConfig: buildSchemeThemeConfig('ocean', 'light'),
          } as ThemeState;
        }

        const state = persistedState as Partial<ThemeState>;
        const mode: ThemeMode = state.mode === 'dark' ? 'dark' : 'light';
        const activeScheme = normalizeThemeScheme(state.activeScheme);

        return {
          ...state,
          mode,
          activeScheme,
          themeConfig: buildSchemeThemeConfig(activeScheme, mode, state.themeConfig),
        };
      },
    }
  )
);
